import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Upload, Download, Trash2, FileText, Eye, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { UnifiedFilePreview } from '@/components/shared/UnifiedFilePreview';
import { DragDropZone } from './DragDropZone';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { logStorageAccessError } from '@/lib/securityLogger';
import { normalizeStoragePath } from '@/lib/storageUtils';

interface LocationDocumentsProps {
  locationId: string;
}

const DOCUMENT_TYPES = [
  { value: 'contract', label: 'Contract' },
  { value: 'permit', label: 'Vergunning' },
  { value: 'drawing', label: 'Tekening' },
  { value: 'photo', label: 'Foto' },
  { value: 'other', label: 'Overig' },
];

const VISIBILITY_TAGS = [
  { value: 'intern', label: 'Intern', description: 'Alleen voor intern gebruik', color: 'bg-red-500' },
  { value: 'extern', label: 'Extern', description: 'Mag gedeeld met NDA-partijen', color: 'bg-orange-500' },
  { value: 'publiek', label: 'Publiek', description: 'Geen gevoelige informatie', color: 'bg-green-500' },
];

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

export function LocationDocuments({ locationId }: LocationDocumentsProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentType, setDocumentType] = useState<string>('other');
  const [description, setDescription] = useState('');
  const [visibilityTag, setVisibilityTag] = useState<string>('');
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [previewFile, setPreviewFile] = useState<any | null>(null);
  const [uploadResetKey, setUploadResetKey] = useState(0);

  const { data: documents = [], isLoading, isError } = useQuery({
    queryKey: ['location-documents', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('location_documents')
        .select(`
          *,
          profiles:uploaded_by(display_name, email)
        `)
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    retry: 1,
  });

  const uploadSingleFile = async (file: File, index: number) => {
    try {
      setFileStatuses(prev => prev.map((f, i) => 
        i === index ? { ...f, status: 'uploading', progress: 10 } : f
      ));
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}/${locationId}/${crypto.randomUUID()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('location-documents')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (uploadError) {
        await logStorageAccessError({
          operation: 'write',
          bucket: 'location-documents',
          path: fileName,
          errorMessage: uploadError.message,
          userId: user?.id,
        });
        throw uploadError;
      }
      
      setFileStatuses(prev => prev.map((f, i) => 
        i === index ? { ...f, progress: 50 } : f
      ));
      
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('location-documents')
        .createSignedUrl(fileName, 60 * 60 * 24 * 7);
      
      if (signedUrlError) {
        await supabase.storage.from('location-documents').remove([fileName]);
        throw signedUrlError;
      }
      
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      
      setFileStatuses(prev => prev.map((f, i) => 
        i === index ? { ...f, progress: 75 } : f
      ));
      
      const { error: dbError } = await supabase
        .from('location_documents')
        .insert({
          location_id: locationId,
          document_name: file.name,
          document_url: signedUrlData.signedUrl,
          storage_path: fileName,
          url_expires_at: expiresAt,
          document_type: documentType,
          file_size: file.size,
          uploaded_by: user?.id,
          description: description || null,
          visibility_tag: visibilityTag || null,
        });
      
      if (dbError) {
        await supabase.storage.from('location-documents').remove([fileName]);
        throw dbError;
      }
      
      setFileStatuses(prev => prev.map((f, i) => 
        i === index ? { ...f, status: 'success', progress: 100 } : f
      ));
    } catch (error: any) {
      setFileStatuses(prev => prev.map((f, i) => 
        i === index ? { ...f, status: 'error', progress: 0, error: error.message } : f
      ));
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast({ title: 'Geen bestanden geselecteerd', variant: 'destructive' });
      return;
    }

    if (!visibilityTag) {
      toast({ title: 'Selecteer een zichtbaarheid', description: 'Kies Intern, Extern of Publiek', variant: 'destructive' });
      return;
    }
    
    setIsUploading(true);
    
    setFileStatuses(selectedFiles.map(file => ({
      file,
      status: 'pending',
      progress: 0,
    })));
    
    const batchSize = 3;
    for (let i = 0; i < selectedFiles.length; i += batchSize) {
      const batch = selectedFiles.slice(i, i + batchSize);
      await Promise.all(
        batch.map((file, batchIndex) => uploadSingleFile(file, i + batchIndex))
      );
    }
    
    const successCount = fileStatuses.filter(f => f.status === 'success').length;
    toast({ 
      title: 'Upload voltooid',
      description: `${successCount} bestand(en) geüpload`
    });
    
    setSelectedFiles([]);
    setFileStatuses([]);
    setDescription('');
    setVisibilityTag('');
    setIsUploading(false);
    setUploadResetKey(prev => prev + 1); // Reset the DragDropZone
    queryClient.invalidateQueries({ queryKey: ['location-documents'] });
  };

  const handleDelete = async (docId: string) => {
    const { error } = await supabase
      .from('location_documents')
      .delete()
      .eq('id', docId);

    if (error) {
      toast({ 
        title: 'Fout bij verwijderen', 
        description: error.message,
        variant: 'destructive' 
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ['location-documents'] });
      toast({ title: 'Document verwijderd' });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: `${file.name} is te groot`,
          description: 'Maximale bestandsgrootte is 10MB',
          variant: 'destructive',
        });
        return false;
      }
      return true;
    });
    
    setSelectedFiles(validFiles);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'dd MMM yyyy HH:mm', { locale: nl });
  };

  const getDocumentTypeLabel = (type: string) => {
    return DOCUMENT_TYPES.find(t => t.value === type)?.label || 'Overig';
  };

  const getVisibilityBadge = (tag: string | null) => {
    if (!tag) return null;
    const config = VISIBILITY_TAGS.find(t => t.value === tag);
    if (!config) return null;
    return (
      <Badge className={`${config.color} text-white text-xs`}>
        {config.label}
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Documenten
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload Section */}
        <div className="border rounded-lg p-4 bg-muted/30">
          <h4 className="text-sm font-medium mb-3">Document(en) uploaden</h4>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm mb-2 block">Document type</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm mb-2 block">Zichtbaarheid *</Label>
                <RadioGroup value={visibilityTag} onValueChange={setVisibilityTag} className="flex gap-2">
                  {VISIBILITY_TAGS.map(tag => (
                    <div key={tag.value} className="flex items-center">
                      <RadioGroupItem value={tag.value} id={`visibility-${tag.value}`} className="sr-only" />
                      <Label
                        htmlFor={`visibility-${tag.value}`}
                        className={`px-3 py-1.5 rounded-full text-xs cursor-pointer border-2 transition-all ${
                          visibilityTag === tag.value 
                            ? `${tag.color} text-white border-transparent` 
                            : 'bg-background border-muted-foreground/20 hover:border-muted-foreground/40'
                        }`}
                      >
                        {tag.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
            
            <div>
              <Label className="text-sm mb-2 block">Beschrijving/Notitie (optioneel)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Korte beschrijving van het document..."
                className="resize-none h-20"
              />
            </div>
            
            <div>
              <Label className="text-sm mb-2 block">Bestanden (max 10MB per bestand)</Label>
              <DragDropZone
                onFilesSelected={(files) => {
                  const validFiles = files.filter((file) => {
                    if (file.size > 10 * 1024 * 1024) {
                      toast({
                        title: `${file.name} is te groot`,
                        description: 'Maximale bestandsgrootte is 10MB',
                        variant: 'destructive',
                      });
                      return false;
                    }
                    return true;
                  });
                  
                  // Append files and enforce 10-file limit
                  setSelectedFiles(prev => {
                    const combined = [...prev, ...validFiles];
                    if (combined.length > 10) {
                      toast({
                        title: 'Maximum bereikt',
                        description: 'Je kunt maximaal 10 bestanden tegelijk uploaden',
                        variant: 'destructive',
                      });
                      return combined.slice(0, 10);
                    }
                    return combined;
                  });
                }}
                accept=".pdf,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
                maxFiles={10}
                maxSize={10}
                resetKey={uploadResetKey}
              />
            </div>
            
            {selectedFiles.length > 0 && !isUploading && (
              <Button 
                onClick={handleUpload} 
                disabled={selectedFiles.length === 0 || isUploading || !visibilityTag}
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload {selectedFiles.length} bestand(en)
              </Button>
            )}
          </div>
          
          {selectedFiles.length > 0 && !isUploading && (
            <div className="space-y-2 mt-3">
              <p className="text-xs text-muted-foreground">{selectedFiles.length} bestand(en) geselecteerd</p>
              {selectedFiles.map((file, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-background rounded">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm flex-1 truncate">{file.name}</span>
                  <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={() => removeFile(idx)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {isUploading && fileStatuses.length > 0 && (
            <div className="space-y-2 mt-3">
              {fileStatuses.map((status, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-background rounded">
                  {status.status === 'success' && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
                  {status.status === 'error' && <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />}
                  {status.status === 'uploading' && <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />}
                  {status.status === 'pending' && <div className="w-4 h-4 border-2 border-muted-foreground/30 rounded-full flex-shrink-0" />}
                  <span className="text-sm flex-1 truncate">{status.file.name}</span>
                  {status.status === 'uploading' && (
                    <Progress value={status.progress} className="w-24 h-1" />
                  )}
                  {status.status === 'error' && (
                    <span className="text-xs text-destructive">{status.error}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Documents List */}
        {isLoading ? (
          <p className="text-center text-muted-foreground py-4">Laden...</p>
        ) : isError ? (
          <p className="text-center text-destructive py-4">Fout bij laden van documenten</p>
        ) : documents.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">Geen documenten geüpload</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc: any) => (
              <div key={doc.id} className="flex items-start justify-between p-3 border rounded-lg hover:bg-muted/50">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">{doc.document_name}</p>
                      <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full flex-shrink-0">
                        {getDocumentTypeLabel(doc.document_type)}
                      </span>
                      {getVisibilityBadge(doc.visibility_tag)}
                    </div>
                    {doc.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{doc.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatFileSize(doc.file_size)} • {formatDate(doc.created_at)}
                      {doc.profiles?.display_name && ` • ${doc.profiles.display_name}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 ml-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewFile({ 
                      file_name: doc.document_name, 
                      file_size: doc.file_size,
                      bucket: 'location-documents',
                      storage_path: doc.storage_path
                    })}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const { data, error } = await supabase.storage
                          .from('location-documents')
                          .download(doc.storage_path);
                        
                        if (error) throw error;
                        
                        const blobUrl = URL.createObjectURL(data);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = doc.document_name || 'download';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(blobUrl);
                        
                        toast({ title: 'Download gestart' });
                      } catch (error: any) {
                        toast({
                          title: 'Fout bij downloaden',
                          description: error.message,
                          variant: 'destructive',
                        });
                      }
                    }}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (confirm('Weet je zeker dat je dit document wilt verwijderen?')) {
                        handleDelete(doc.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Preview Modal */}
        {previewFile && (
          <UnifiedFilePreview
            file={{
              name: previewFile.file_name,
              bucket: previewFile.bucket,
              path: previewFile.storage_path,
              size: previewFile.file_size,
            }}
            open={!!previewFile}
            onOpenChange={(open) => !open && setPreviewFile(null)}
            portal="charging"
          />
        )}
      </CardContent>
    </Card>
  );
}